import type { FormEvent } from 'react';
import React, { useMemo } from 'react';

import { typography } from '@pray/shared/components/foundations';
import AppBar from '@pray/shared/components/ui/AppBar/AppBar';
import Button from '@pray/shared/components/ui/Button';
import { CheckCircle } from '@pray/shared/components/ui/Icons/CheckCircle';
import Text from '@pray/shared/components/ui/Text';
import { EVENTS } from '@pray/shared/constants';
import { cn } from '@pray/shared/utils/styles';

import {
  donationIntervals,
  minMonthlyDonationAmount,
  minAnnuallyDonationAmount,
  COPY_UPDATE_FUND_OVERRIDES,
} from '../constants';
import type { AmountFieldType, DonationForm, Fund } from '../types';
import DonationAmountPresets from './components/DonationAmountPresets/DonationAmountPresets';
import DonationInterval from './components/DonationInterval/DonationInterval';
import DonationOptions from './components/DonationOptions/DonationOptions';
import FundDetails from './components/FundDetails/FundDetails';
import FundImage from './components/FundImage/FundImage';
import InlineCustomAmount from './components/InlineCustomAmount/InlineCustomAmount';

import fundImageStyles from './components/FundImage/FundImage.module.scss';
import styles from './DonationAmountPage.module.scss';

// Shared across every fund, so it must not name a real person. Mirrors the server's
// own 'Your Leader' default for artist-less fund copy.
const DEFAULT_ARTIST_NAME = 'Your leader';

const SUBSCRIPTION_BENEFITS = [
  'Over 3,500 Audio Bible Stories, Devotionals, Testimonies and more',
  'Listen to Bible in a Year with Jack Graham',
  'Receive a daily prayer to strengthen your prayer practice',
];

interface DonationAmountPageProps {
  form: DonationForm;
  fund?: Fund;
  title?: string;
  subtitle?: string;
  description?: string;
  ctaText?: string;
  heroImage?: string;
  backgroundColor?: string;
  fontColor?: string;
  isPreview?: boolean;
  showAppBar?: boolean;
  isLoading?: boolean;
  showCustomAmountButton?: boolean;
  isCopyUpdateVariation?: boolean;
  getAmountFieldType?: () => AmountFieldType;
  onContinue?: () => void;
  onCustomAmountSelect?: () => void;
  onPresetAmountSelect?: (amount: string | null) => void;
}

interface DonationAmountProps {
  form: DonationForm;
  fund: Fund;
  type: AmountFieldType;
  isValid: boolean;
  minimumDonationAmount: number;
  showCustomAmountButton?: boolean;
  onCustomAmountSelect?: () => void;
  onPresetAmountSelect?: (amount: string | null) => void;
}

export default function DonationAmountPage({
  form,
  fund = {},
  title = '',
  subtitle = '',
  description,
  ctaText = 'Continue',
  heroImage = '',
  backgroundColor = '',
  fontColor = '',
  isPreview = false,
  showAppBar = true,
  isLoading = false,
  showCustomAmountButton = false,
  isCopyUpdateVariation = false,
  getAmountFieldType = () => 'default',
  onContinue,
  onCustomAmountSelect,
  onPresetAmountSelect,
}: DonationAmountPageProps): JSX.Element | null {
  const isMonthly = form.values.interval === donationIntervals.month;
  const minimumDonationAmount = isMonthly ? minMonthlyDonationAmount : minAnnuallyDonationAmount;

  const isValidAmount = useMemo(() => {
    return form.values.amount >= minimumDonationAmount;
  }, [form.values.amount, minimumDonationAmount]);

  const amountFieldType = useMemo(() => getAmountFieldType(), [getAmountFieldType]);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (isValidAmount && onContinue) onContinue();
  }

  // Use heroImage if provided, otherwise fall back to fund.image_url
  const imageUrl = heroImage || fund.image_url;

  // Already on the fund payload where the server sends it — no extra request.
  const artistImageUrl = fund.artist_profile_horizontal_image_url;

  return (
    <div
      className={styles.container}
      data-viewname={EVENTS.PRAY_EVENTS.VIEW_EVENT_NAMES.DONATION_FLOW.DONATION_PAYWALL}
      style={
        {
          ...(backgroundColor ? { '--donation-bg-color': backgroundColor } : {}),
          ...(fontColor ? { '--donation-font-color': fontColor } : {}),
        } as React.CSSProperties
      }
    >
      {!isPreview && showAppBar && (
        <div className={cn(styles.appBarContainer, backgroundColor && '[&_svg_path]:fill-current')}>
          <AppBar transparent={!!backgroundColor} />
        </div>
      )}

      <div className="relative">
        <FundImage
          url={imageUrl}
          artistImageUrl={artistImageUrl}
          isLoading={isLoading}
          className={backgroundColor ? fundImageStyles.noMask : undefined}
        />

        {isCopyUpdateVariation && (
          <div className="absolute inset-x-0 top-0 z-10 flex flex-col px-[18px] pb-5 pt-20">
            <SubscriptionBenefits artistName={fund.artist_name || DEFAULT_ARTIST_NAME} />
            <DonationInterval form={form} fund={{ ...fund, ...COPY_UPDATE_FUND_OVERRIDES }} />
            <div className="mt-3">
              <DonationAmount
                form={form}
                fund={{ ...fund, ...COPY_UPDATE_FUND_OVERRIDES }}
                type={amountFieldType}
                isValid={isValidAmount}
                minimumDonationAmount={minimumDonationAmount}
                showCustomAmountButton={false}
                onCustomAmountSelect={onCustomAmountSelect}
                onPresetAmountSelect={onPresetAmountSelect}
              />
            </div>
          </div>
        )}
      </div>

      <form className={styles.content} onSubmit={handleSubmit}>
        {!isCopyUpdateVariation && (
          <>
            <FundDetails
              isLoading={isLoading}
              title={title || fund.name}
              subtitle={subtitle}
              description={description ?? fund.description}
            />
            <DonationInterval form={form} fund={fund} />
            <DonationAmount
              form={form}
              fund={fund}
              type={amountFieldType}
              isValid={isValidAmount}
              minimumDonationAmount={minimumDonationAmount}
              showCustomAmountButton={showCustomAmountButton}
              onCustomAmountSelect={onCustomAmountSelect}
              onPresetAmountSelect={onPresetAmountSelect}
            />
          </>
        )}

        {!isCopyUpdateVariation && amountFieldType !== 'preset' && (
          <Button disabled={!isValidAmount} className={styles.continue} onClick={onContinue} type="button">
            {ctaText}
          </Button>
        )}
      </form>

      {isCopyUpdateVariation && amountFieldType !== 'preset' && (
        <Button
          disabled={!isValidAmount}
          className={cn(styles.continueStandalone, styles.continueFixed)}
          onClick={onContinue}
          type="button"
        >
          {ctaText}
        </Button>
      )}
    </div>
  );
}

// Component for the subscription benefits list
function SubscriptionBenefits({ artistName }: { artistName: string }) {
  return (
    <>
      <Text variant={typography.heading_2xl} className="mb-6 text-left text-white" block>
        {artistName} is gifting you a Pray.com subscription with your donation
      </Text>

      <div className="mb-4 w-full space-y-4 text-left">
        {SUBSCRIPTION_BENEFITS.map((benefit) => (
          <div key={benefit} className="flex items-start space-x-3">
            <CheckCircle fill="#e3af4a" width="20" height="20" className="mt-0.5 shrink-0" />
            <Text variant={typography.body} className="text-white">
              {benefit}
            </Text>
          </div>
        ))}
      </div>
    </>
  );
}

function DonationAmount({
  form,
  fund,
  type,
  isValid,
  minimumDonationAmount,
  showCustomAmountButton,
  onCustomAmountSelect,
  onPresetAmountSelect,
}: DonationAmountProps): JSX.Element {
  if (type === 'custom') {
    return <InlineCustomAmount form={form} isValidAmount={isValid} minimumDonationAmount={minimumDonationAmount} />;
  }

  if (type === 'preset') {
    return <DonationAmountPresets interval={form.values.interval} onSelectAmount={onPresetAmountSelect} />;
  }

  return (
    <DonationOptions
      form={form}
      fund={fund}
      showCustomAmountButton={showCustomAmountButton}
      onCustomAmountSelect={onCustomAmountSelect}
    />
  );
}
